import React from 'react';
import { useUser } from '../../context/UserContext';

/**
 * AdminGuard - Conditionally renders children based on role OR capability (DOS-26, DOS-213).
 *
 * Role-based (legacy):
 *   <AdminGuard requiredRole="org_admin"><AdminOnlyContent /></AdminGuard>
 *
 * Capability-based (preferred for new code):
 *   <AdminGuard requiredCapability="edit_context"><EditContextButton /></AdminGuard>
 *   <AdminGuard requiredAnyCapability={['edit_context','edit_context_own_location']}>...
 *
 * If both `requiredCapability` and `requiredRole` are provided, capability check wins.
 * Renders `fallback` (default: null) when unauthorized.
 *
 * Role hierarchy: superadmin > org_admin > location_admin > manager > user
 */
function AdminGuard({
  requiredRole,
  requiredCapability,
  requiredAnyCapability,
  fallback = null,
  children,
}) {
  const { hasRole, hasCapability, hasAnyCapability, loading } = useUser();

  if (loading) return null;

  // Capability-based check (preferred)
  if (requiredCapability) {
    return hasCapability(requiredCapability) ? <>{children}</> : fallback;
  }
  if (Array.isArray(requiredAnyCapability) && requiredAnyCapability.length > 0) {
    return hasAnyCapability(...requiredAnyCapability) ? <>{children}</> : fallback;
  }

  // Role-based fallback
  if (requiredRole) {
    return hasRole(requiredRole) ? <>{children}</> : fallback;
  }

  // No guard configured — default to org_admin role (backwards-compat with original default)
  return hasRole('org_admin') ? <>{children}</> : fallback;
}

export default AdminGuard;
